'use client';

import {
  ChangeEvent,
  useActionState,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import DrugModal from './DrugModal';
import styles from 'styles/sunfire.forms.module.css';
import { Packaging, Rx } from 'types/SunFire';
import { getRxPackaging, handleRxLookupForm, searchRx } from 'actions/Sunfire';
import { DBRx } from 'db/schema/drugs';
import { useAppDispatch } from 'lib/hooks';
import { ANALYTICS_EVENTS, capture } from 'lib/analytics';
import { addPrescription } from 'slices/prescriptionSlice';
import CaretIcon from 'components/icons/CaretIcon';

interface DrugSearchProps {
  title: string;
  subtitle: string;
}

const DrugSearch = ({ title, subtitle }: DrugSearchProps) => {
  const dispatch = useAppDispatch();

  const [packaging, setPackaging] = useState<Packaging[]>([]);
  const [selectedDrug, setSelectedDrug] = useState<Rx | undefined>(undefined);
  const [drug, setDrug] = useState<string>('');
  const [results, setResults] = useState<[]>([]);
  const modalRef = useRef<HTMLDialogElement | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  const [formState] = useActionState(handleRxLookupForm, {
    drug: '',
    results: [],
  });

  useEffect(() => {
    queueMicrotask(() => {
      setDrug(formState.drug);
      setResults(formState.results);
    });
  }, [formState, setDrug, setResults]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const clearState = () => {
    setSelectedDrug(undefined);
    setPackaging([]);
    setDrug('');
    setResults([]);
    closeModal();
  };

  const prescriptionAdded = (drug: DBRx) => {
    dispatch(addPrescription(drug));
    capture(ANALYTICS_EVENTS.prescriptionSaved);
    clearState();
  };

  const getDrugDetails = async (drug: Rx) => {
    // Pull the results
    setSelectedDrug(drug);
    const packaging = await getRxPackaging(drug.id);
    setPackaging(packaging);

    if (modalRef.current) {
      modalRef.current.showModal();
    }
  };

  const handleDrugChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (value.length > 2) {
        capture(ANALYTICS_EVENTS.prescriptionSearchPerformed);
        const results = await searchRx(value);
        setResults(results);
      } else {
        // Clear results when input is cleared or too short
        setResults([]);
      }
    },
    []
  );

  return (
    <>
      <form className="has-divider">
        <fieldset>
          <div className="form-header">
            <legend>{title}</legend>
            <p className="explanation-text">{subtitle}</p>
          </div>
          <div ref={searchContainerRef}>
            <div className="row">
              <label htmlFor="drug-name" className="field-label">
                Medication name
              </label>
              <input
                id="drug-name"
                type="search"
                name="drug"
                defaultValue={drug}
                placeholder="Start typing a medication name to search..."
                autoComplete="off"
                onChange={handleDrugChange}
              />
            </div>
            <div className={styles.rxMenuContainer}>
              {results.length > 0 && (
                <menu className={`${styles.autocomplete} ${styles.rxMenu}`}>
                  {results.map((drug: Rx, idx: number) => {
                    return (
                      <li key={idx}>
                        <button
                          type="button"
                          className={styles.autocompleteBtn}
                          onClick={() => getDrugDetails(drug)}
                        >
                          <p>
                            <b>{drug.name}</b>
                          </p>
                          <CaretIcon size="large" direction="right" />
                        </button>
                      </li>
                    );
                  })}
                </menu>
              )}
            </div>
          </div>
        </fieldset>
      </form>

      <DrugModal
        modalRef={modalRef}
        drug={selectedDrug}
        packaging={packaging}
        addPrescription={prescriptionAdded}
        onModalClose={closeModal}
      />
    </>
  );
};

export default DrugSearch;
